import React, { forwardRef } from "react"
import { useTableCtx } from "./TableContext"
import { Checkbox } from "antd"
import Icon from "../Icon"
import tw, { css, theme } from "twin.macro"
import { map, size, isPlainObject } from "lodash"
import { clsx } from "@/utilities"
import { COL_SELECTION_WIDTH, ROW_HEADER_HEIGHT } from "./helper"

const SORT_ICON_SIZES = [8, 4]

const ItemRowHeaderRenderer = forwardRef(function I({ style }, ref) {
    const {
        columns,
        sorter,
        rowSelection: { isAllSelected, onSelectAll } = {},
        colWidthAsPercents,
        onSort,
        getItemSize,
        measure,
        isMeasuring: checkIsMeasuring,
        isMeasured: checkIsMeasured,
        expandable
    } = useTableCtx()

    const rowHeight = getItemSize(0)
    const isMeasuring = checkIsMeasuring(0)
    const isMeasured = checkIsMeasured(0)

    const isBtnExpandDisplayed = isPlainObject(expandable)
    const allRowKeys = expandable?.expandableRowKeys
    const isExpanded = size(expandable?.expandedRowKeys) === size(allRowKeys)

    const handleClick = () => onSelectAll(!isAllSelected)

    return (
        <div
            ref={ref}
            css={css(style)}
            className={clsx("table-thead")}
            data-measuring={isMeasuring}
            data-measured-size={rowHeight}
        >
            <div
                ref={(ref) => measure(0, ref)}
                className={clsx("table-header-row", "table-row")}
                {...(isMeasured && { style: { height: rowHeight } })}
            >
                {typeof onSelectAll === "function" && (
                    <div
                        tw="center"
                        className="table-header-cell table-cell table-selection-column"
                        css={css({
                            width: colWidthAsPercents[0] + "%",
                            minWidth: COL_SELECTION_WIDTH
                        })}
                    >
                        <Checkbox checked={isAllSelected} onClick={handleClick} />
                    </div>
                )}
                {map(columns, ({ key, sorting: isSorting, ...column }, colIndex) => {
                    const sortOrder = sorter[key]
                    const wColIndex = colIndex + 1
                    const sortSequence = Object.keys(sorter).indexOf(key) + 1

                    return (
                        <div
                            key={key}
                            css={[
                                css({
                                    width: colWidthAsPercents[wColIndex] + "%",
                                    minWidth: column.width,
                                    textAlign: column.align
                                }),
                                isSorting && tw`relative`
                            ]}
                            className={clsx(
                                "table-header-cell table-cell",
                                isSorting && "table-header-cell-has-sorters"
                            )}
                            onClick={() => (isSorting ? onSort?.(key) : null)}
                        >
                            {isSorting ? (
                                <div style={{ width: "calc(100% - 18px)", userSelect: "none" }}>
                                    {column.title}
                                </div>
                            ) : (
                                column.title
                            )}
                            {isSorting && (
                                <>
                                    <div
                                        tw="absolute transform top-1/2 -translate-y-1/2"
                                        css={[sortSequence ? tw`right-4` : tw`right-2`]}
                                    >
                                        <Icon
                                            tw="transform rotate-180 mb-1"
                                            name="sort_down"
                                            sizes={SORT_ICON_SIZES}
                                            {...(sortOrder === "asc" && {
                                                color: theme`colors.gray800`
                                            })}
                                        />
                                        <Icon
                                            name="sort_down"
                                            sizes={SORT_ICON_SIZES}
                                            {...(sortOrder === "desc" && {
                                                color: theme`colors.gray800`
                                            })}
                                        />
                                    </div>
                                    <div tw="absolute right-2 text-[8px] mt-1.5">
                                        {!!sortSequence && sortSequence}
                                    </div>
                                </>
                            )}
                        </div>
                    )
                })}
                {isBtnExpandDisplayed && (
                    <div
                        className={"table-header-cell table-cell table-cell-arrow"}
                        data-expanded={isExpanded}
                        onClick={() => expandable?.onExpand(isExpanded ? [] : allRowKeys)}
                    >
                        <Icon name="arrow_right" />
                    </div>
                )}
            </div>
        </div>
    )
})

export default forwardRef(function I({ children, ...rest }, ref) {
    return (
        <div ref={ref} {...rest}>
            <ItemRowHeaderRenderer
                key={"thead"}
                style={{
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: ROW_HEADER_HEIGHT
                }}
            />
            {children}
        </div>
    )
})
